// src/components/upmode-agent/chat-message.tsx
import React from "react";
import { User, Wrench } from "lucide-react";
import { UpmodeMessage } from "@/app/services/upmode-agent/upmode-agent-api";

interface ChatMessageProps {
  message: UpmodeMessage;
  agentEmoji?: string;
  streaming?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Markdown renderer — supports headings, lists, code fences, inline formatting,
// links, AND GitHub-style tables.
// ────────────────────────────────────────────────────────────────────────────

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith("|")) trimmed = trimmed.slice(1);
  if (trimmed.endsWith("|")) trimmed = trimmed.slice(0, -1);
  return trimmed.split("|").map(c => c.trim());
}

function parseAlignment(separator: string): Array<"left" | "center" | "right"> {
  return splitTableRow(separator).map(cell => {
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });
}

function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // ── Fenced code block ────────────────────────────────────────────────
    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]); i++;
      }
      i++;
      elements.push(
        <pre key={key++} style={{ backgroundColor: "rgba(0,0,0,0.06)", padding: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.55, overflowX: "auto", margin: "6px 0", fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace", color: "var(--foreground)" }}>
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // ── GitHub-style tables ──────────────────────────────────────────────
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const headerCells = splitTableRow(line);
      const alignments = parseAlignment(lines[i + 1]);
      i += 2;

      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        bodyRows.push(splitTableRow(lines[i]));
        i++;
      }

      elements.push(
        <div key={key++} style={{ overflowX: "auto", margin: "8px 0", border: "1px solid var(--border)", borderRadius: 8, maxWidth: "100%" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ backgroundColor: "rgba(13,148,136,0.06)" }}>
                {headerCells.map((h, idx) => (
                  <th
                    key={idx}
                    style={{
                      padding: "8px 10px",
                      textAlign: alignments[idx] || "left",
                      fontWeight: 700,
                      color: "var(--foreground)",
                      borderBottom: "1px solid var(--border)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rIdx) => (
                <tr
                  key={rIdx}
                  style={{
                    backgroundColor: rIdx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  }}
                >
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      style={{
                        padding: "7px 10px",
                        textAlign: alignments[cIdx] || "left",
                        color: "var(--foreground)",
                        borderTop: rIdx === 0 ? "none" : "1px solid var(--border)",
                        verticalAlign: "top",
                      }}
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // ── Headings ─────────────────────────────────────────────────────────
    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      const fontSize = level === 1 ? 16 : level === 2 ? 15 : 14;
      elements.push(
        <div key={key++} style={{ fontSize, fontWeight: 700, margin: "8px 0 4px", color: "var(--foreground)" }}>
          {renderInline(content)}
        </div>
      );
      i++;
      continue;
    }

    // ── Bulleted list ────────────────────────────────────────────────────
    if (/^[-*]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(<li key={key++} style={{ marginBottom: 2 }}>{renderInline(lines[i].replace(/^[-*]\s+/, ""))}</li>);
        i++;
      }
      elements.push(
        <ul key={key++} style={{ margin: "4px 0 4px 18px", padding: 0, listStyle: "disc" }}>{items}</ul>
      );
      continue;
    }

    // ── Numbered list ────────────────────────────────────────────────────
    if (/^\d+\.\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(<li key={key++} style={{ marginBottom: 2 }}>{renderInline(lines[i].replace(/^\d+\.\s+/, ""))}</li>);
        i++;
      }
      elements.push(
        <ol key={key++} style={{ margin: "4px 0 4px 20px", padding: 0 }}>{items}</ol>
      );
      continue;
    }

    // ── Blank line ───────────────────────────────────────────────────────
    if (line.trim() === "") {
      elements.push(<div key={key++} style={{ height: 6 }} />);
      i++;
      continue;
    }

    // ── Regular paragraph ────────────────────────────────────────────────
    elements.push(
      <p key={key++} style={{ margin: "2px 0", lineHeight: 1.55 }}>{renderInline(line)}</p>
    );
    i++;
  }

  return elements;
}

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const code = remaining.match(/`([^`]+)`/);
    const bold = remaining.match(/\*\*([^*]+)\*\*/);
    const italic = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
    const link = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);

    const candidates = [
      code && { type: "code", match: code, start: remaining.indexOf(code[0]) },
      bold && { type: "bold", match: bold, start: remaining.indexOf(bold[0]) },
      italic && { type: "italic", match: italic, start: remaining.indexOf(italic[0]) },
      link && { type: "link", match: link, start: remaining.indexOf(link[0]) },
    ].filter(Boolean) as Array<{ type: string; match: RegExpMatchArray; start: number }>;

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    const earliest = candidates.reduce((a, b) => (a.start < b.start ? a : b));
    if (earliest.start > 0) parts.push(remaining.slice(0, earliest.start));

    const m = earliest.match;
    if (earliest.type === "code") {
      parts.push(<code key={key++} style={{ backgroundColor: "rgba(0,0,0,0.06)", padding: "1px 5px", borderRadius: 3, fontSize: "0.92em", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{m[1]}</code>);
    } else if (earliest.type === "bold") {
      parts.push(<strong key={key++}>{m[1]}</strong>);
    } else if (earliest.type === "italic") {
      parts.push(<em key={key++}>{m[1]}</em>);
    } else if (earliest.type === "link") {
      parts.push(<a key={key++} href={m[2]} target="_blank" rel="noreferrer" style={{ color: "#0D9488", textDecoration: "underline", overflowWrap: "anywhere", wordBreak: "break-word" }}>{m[1]}</a>);
    }

    remaining = remaining.slice(earliest.start + m[0].length);
  }

  return <>{parts}</>;
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export function ChatMessage({ message, agentEmoji, streaming }: ChatMessageProps) {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";

  const hasContent = !!(message.content && message.content.trim());
  const hasToolCalls = message.tool_calls && message.tool_calls.length > 0;

  if (isAssistant && !hasContent && !hasToolCalls) return null;

  // Historical intermediate assistant turns (tool calls only, no text)
  if (isAssistant && !hasContent && hasToolCalls) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 38, maxWidth: "100%" }}>
        {message.tool_calls.map((tc, i) => (
          <span
            key={i}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, color: "#2563EB", fontWeight: 500,
              padding: "3px 9px", borderRadius: 999,
              backgroundColor: "rgba(37,99,235,0.08)",
              border: "1px solid rgba(37,99,235,0.18)",
              alignSelf: "flex-start",
              maxWidth: "100%",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            <Wrench style={{ width: 10, height: 10, flexShrink: 0 }} />
            <span>Called <strong>{tc.name}</strong></span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: isUser ? "row-reverse" : "row",
      gap: 10,
      maxWidth: "100%",
      alignItems: "flex-start",
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        backgroundColor: isUser ? "rgba(37,99,235,0.1)" : "rgba(13,148,136,0.1)",
        border: `1px solid ${isUser ? "rgba(37,99,235,0.2)" : "rgba(13,148,136,0.2)"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14,
      }}>
        {isUser
          ? <User style={{ width: 14, height: 14, color: "#2563EB" }} />
          : <span style={{ lineHeight: 1 }}>{agentEmoji || "🤖"}</span>
        }
      </div>

      {/* Bubble — bumped user to 85% (was 78%) for more mobile breathing room */}
      <div style={{
        maxWidth: isUser ? "85%" : "92%",
        padding: "9px 13px",
        borderRadius: 12,
        backgroundColor: isUser ? "rgba(37,99,235,0.08)" : "var(--muted)",
        border: `1px solid ${isUser ? "rgba(37,99,235,0.2)" : "var(--border)"}`,
        color: "var(--foreground)",
        fontSize: 13,
        lineHeight: 1.55,
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        minWidth: 0,
      }}>
        {isAssistant
          ? renderMarkdown(message.content || "")
          : <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word" }}>{message.content}</div>
        }
        {streaming && <span style={{ display: "inline-block", width: 7, height: 14, backgroundColor: "#0D9488", marginLeft: 3, animation: "blink 1s step-end infinite", verticalAlign: "text-bottom" }} />}
      </div>

      <style>{`
        @keyframes blink { 50% { opacity: 0; } }
      `}</style>
    </div>
  );
}